/**
 * THE FIGURES STATISTICS DRAWS, ONCE (design 18): what each is called, how one and many of it are
 * worded, what the list beside the chart is titled when it ranks by it, and where each read keeps
 * it - the period's tile (`getDashboard`), an article's row (`listArticleStats`), a day
 * (`getDashboardDays`). The Overview's switch, the chart, the ranked list and the Articles tab all
 * read their words from here, so "Countable views" is the same two words everywhere - the name the
 * staff console uses (the owner, 2026-10-19).
 *
 * ★ READERS ARE NOT A FIGURE OF AN ARTICLE'S ROW. A reader who came back on three days would be
 * three readers summed over the period (authors-api.yaml, `listArticleStats`), so the row has no such
 * field and `rowTile` answers nothing for it; the list beside the chart ranks by views when Readers
 * is pressed, and says why (the owner, 2026-10-05, ruling 4). A DAY's readers are that day's alone
 * and are drawn per day, never added up.
 */
import type {
  ArticleStats,
  Dashboard,
  DashboardDay,
  MetricTile,
} from '../../../features/cabinet/api';
import { formatCount, formatPeriod } from '../../../features/cabinet/format';
import { daysIn } from '../../../features/cabinet/ranges';
import type { DayRange, PeriodKind } from '../../../features/cabinet/ranges';
import type { ArticlesT } from '../articles-i18n';

export type FigureKey = 'views' | 'countedViews' | 'readers' | 'likes' | 'comments' | 'saves';

/** A figure an article's row carries - every figure but readers. */
export type ArticleFigureKey = Exclude<FigureKey, 'readers'>;

/** A figure's name: "Countable views". */
export function figureLabel(key: FigureKey, t: ArticlesT): string {
  return t(`stats.figures.${key}.label`);
}

/**
 * The badge drawn beside a figure's name wherever it is named: "Beta" on Countable views (the owner,
 * 2026-10-19: readers still on the old apps are not counted yet, so the number is catching up - its
 * (i) says so). The other figures have none.
 */
export function figureBadge(key: FigureKey, t: ArticlesT): string | undefined {
  return key === 'countedViews' ? t('stats.figures.countedViews.badge') : undefined;
}

/** The ranked list's title when it ranks by this figure: "Most liked". */
export function figureMost(key: FigureKey, t: ArticlesT): string {
  return t(`stats.figures.${key}.most`);
}

/** What the chart says when every day is 0: "No likes on any of these days." */
export function figureNoneOnDays(key: FigureKey, t: ArticlesT): string {
  return t(`stats.figures.${key}.noneOnDays`);
}

/** The Overview's headline figures, in the switch's order (design 18, section 3). */
export const HEADLINE_FIGURES: readonly FigureKey[] = [
  'views',
  'countedViews',
  'readers',
  'likes',
  'comments',
];

/** One article's figures, in the day-by-day panel's switch. */
export const ARTICLE_FIGURES: readonly ArticleFigureKey[] = [
  'views',
  'countedViews',
  'likes',
  'comments',
  'saves',
];

/** "1 view", "1,204 views". */
export function counted(value: number, key: FigureKey, t: ArticlesT): string {
  return t(`stats.figures.${key}.counted`, { count: value, value: formatCount(value) });
}

/** The period's tile for a figure - readers' own shape folded into the common one. */
export function dashboardTile(dashboard: Dashboard, key: FigureKey): MetricTile {
  const tiles = dashboard.tiles;
  if (key === 'readers') {
    const readers = tiles.readers;
    return readers.distinctReadersInRange === undefined || readers.distinctReadersInRange === null
      ? { state: readers.state }
      : { state: readers.state, value: readers.distinctReadersInRange };
  }
  return tiles[key];
}

/** An article's tile for a figure; none for readers (see the header). */
export function rowTile(row: ArticleStats, key: FigureKey): MetricTile | undefined {
  return key === 'readers' ? undefined : row[key];
}

/** A day's figure. Always a number: the read answers `0` for a day with nothing counted. */
export function dayValue(day: DashboardDay, key: FigureKey): number {
  return day[key];
}

/**
 * A tile as a number to draw or compare: `ok` is its value, `no_data` a TRUE 0 (the range really is
 * empty), and `not_reported_yet` nothing at all - never a zero standing in for a figure nobody
 * measures.
 */
export function figureOf(tile: MetricTile | undefined): number | undefined {
  if (tile === undefined) {
    return undefined;
  }
  if (tile.state === 'no_data') {
    return 0;
  }
  return tile.state === 'ok' && typeof tile.value === 'number' ? tile.value : undefined;
}

/** What the ranked list ranks by for a pressed figure: readers are not per article, so views. */
export function rankedBy(key: FigureKey): ArticleFigureKey {
  return key === 'readers' ? 'views' : key;
}

/** How a period is named in a sentence and in a legend (design 18, section 3). */
export interface PeriodWords {
  /** "in these 30 days", "in October 2026 so far". */
  readonly inThese: string;
  /** The chart's legend for the period: "These 30 days", "October 2026 so far". */
  readonly these: string;
  /** The legend for the period before: "The 30 days before". */
  readonly before: string;
  /** The period before inside a sentence: "the 30 days before". */
  readonly beforeInSentence: string;
}

/*
 * One day is said in its own words ("The day before"), so the counted forms are only ever asked for
 * two or more: the catalogue has their `_other` form in English, and each language its own forms.
 */
export function periodWords(
  kind: PeriodKind | 'usual',
  range: DayRange,
  t: ArticlesT,
): PeriodWords {
  const length = daysIn(range);
  const one = length === 1;
  const days = { count: length, value: formatCount(length) };
  const before = one ? t('stats.period.theDayBefore') : t('stats.period.theDaysBefore', days);
  const beforeInSentence = one
    ? t('stats.period.dayBeforeInSentence')
    : t('stats.period.daysBeforeInSentence', days);
  if (kind === 'thisMonth') {
    const month = t('stats.period.monthSoFar', { month: formatPeriod(range.to.slice(0, 7)) });
    return {
      inThese: t('stats.period.inMonth', { month }),
      these: month,
      before,
      beforeInSentence,
    };
  }
  return {
    inThese: one ? t('stats.period.onThisDay') : t('stats.period.inTheseDays', days),
    these: one ? t('stats.period.thisDay') : t('stats.period.theseDays', days),
    before,
    beforeInSentence,
  };
}
